import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { ConstantUtil } from '@/utils/ConstantUtil'
import {
  ILoginParams,
  ILoginResponse,
  IUserProfile,
  IRegisterStartParams,
  IRegisterStartResponse,
  ISendSmsVerificationParams,
  ISendSmsVerificationResponse,
  IRegisterCompleteResponse,
  IRegisterCompleteParams,
  IUpdateUserProfileResponse,
  IUpdateUserProfileParams,
  IRefreshTokenResponse
} from '@/types/auth'
import { deviceService } from '@/utils/deviceService'
import { LogService } from '@/utils/logService'
// import { encryptionService } from '@/services/encryption/encryptionService'
// import { clearImageViewer } from 'antd-mobile/es/components/image-viewer/methods'

// ===============================
// 🔧 基礎設定
// ===============================
const baseQuery = fetchBaseQuery({
  baseUrl: ConstantUtil.API_BASE_URL,
  prepareHeaders: (headers) => {
    // 1️⃣ 加入 Token
    const token = localStorage.getItem(ConstantUtil.STORAGE_USER_TOKEN)
    if (token) {
      headers.set('authorization', `Bearer ${token}`)
      try {
        const tokenPayload = JSON.parse(atob(token.split('.')[1]))
        LogService.info('Token 使用追蹤', {
          userId: tokenPayload.sub || tokenPayload.id,
          userType: tokenPayload.type || 'unknown',
          tokenType: typeof tokenPayload.sub || typeof tokenPayload.id,
          issuedAt: new Date(tokenPayload.iat * 1000).toISOString(),
          expiresAt: new Date(tokenPayload.exp * 1000).toISOString(),
        })
      } catch (error) {
        LogService.warn('無法解析 JWT token', { error: (error as Error).message })
      }
    }

    // 2️⃣ 加入裝置資訊
    const deviceData = deviceService.getDeviceInfo()
    headers.set('devicedata', encodeURIComponent(JSON.stringify(deviceData)))

    // 3️⃣ 設定內容類型
    headers.set('Content-Type', 'application/json')

    return headers
  },
  timeout: ConstantUtil.API_TIMEOUT,
})

const baseQueryWithEncryption = async (args: any, api: any, extraOptions: any) => {
  let result = await baseQuery(args, api, extraOptions);

  // ✅ Token 過期的情況
  if (result.error && result.error.status === 401) {
    console.warn('⚠️ Access Token 過期，開始嘗試 Refresh Token');

    const refreshToken = localStorage.getItem(ConstantUtil.STORAGE_USER_REFRESH_TOKEN);
    if (!refreshToken) return result; 

    const rawBaseQuery = fetchBaseQuery({
      baseUrl: ConstantUtil.API_BASE_URL,
      prepareHeaders: (headers) => {
        headers.set('Content-Type', 'application/json');
        return headers;
      }
    });

    const refreshResult = await rawBaseQuery(
      {
        url: ConstantUtil.API_REFRESH_TOKEN,
        method: 'POST',
        body: { refreshToken },
      },
      api,
      extraOptions
    );

    // ✅ 型別斷言（這裡就是解決你 "tokens 不存在" 的錯誤）
    const refreshBody = refreshResult.data as IRefreshTokenResponse | undefined;
    const refreshData = refreshBody?.data;
    if (refreshData?.accessToken) {
      const { accessToken, refreshToken: newRefreshToken } = refreshData;
      // ✅ 儲存新 token
      localStorage.setItem(ConstantUtil.STORAGE_USER_TOKEN, accessToken);
      if (newRefreshToken) {
        localStorage.setItem(ConstantUtil.STORAGE_USER_REFRESH_TOKEN, newRefreshToken);
      }

      // 🔁 用新 accessToken 重試原 request
      return await baseQuery(args, api, extraOptions);
    }


    alert('您的登入已逾時，請重新登入');

    localStorage.removeItem(ConstantUtil.STORAGE_USER_TOKEN);
    localStorage.removeItem(ConstantUtil.STORAGE_USER_REFRESH_TOKEN);
    localStorage.removeItem(ConstantUtil.STORAGE_USER_INFO);
    window.location.href = '/login';
  }

  return result;
};




// ===============================
// 👤 認證 API
// ===============================
export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery: baseQueryWithEncryption,
  tagTypes: ['User'],
  endpoints: (builder) => ({
    // 登入
    login: builder.mutation<ILoginResponse, ILoginParams>({
      query: (credentials) => ({
        url: ConstantUtil.API_USER_LOGIN,
        method: 'POST',
        body: credentials,
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          LogService.logApiCall(
            ConstantUtil.API_USER_LOGIN,
            'POST',
            undefined,
            data as unknown as Record<string, unknown>
          )
        } catch (error) {
          LogService.error('Login API call failed', error)
        }
      },
      invalidatesTags: ['User'],
    }),
    // 發送註冊驗證碼前啟動（取得 sessionId）
    registerStart: builder.mutation<IRegisterStartResponse, IRegisterStartParams>({
      query: (params) => ({
        url: ConstantUtil.API_USER_REGISTER_START,
        method: 'POST',
        body: params,
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          LogService.logApiCall(
            ConstantUtil.API_USER_REGISTER_START,
            'POST',
            undefined,
            data as unknown as Record<string, unknown>
          )
        } catch (error) {
          LogService.error('Register start API call failed', error)
        }
      },
    }),

    // 發送簡訊驗證碼
    sendSmsVerification: builder.mutation<ISendSmsVerificationResponse, ISendSmsVerificationParams>({
      query: (params) => ({
        url: ConstantUtil.API_USER_SEND_SMS_VERIFICATION,
        method: 'POST',
        body: params,
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          LogService.logApiCall(
            ConstantUtil.API_USER_SEND_SMS_VERIFICATION,
            'POST',
            undefined,
            data as unknown as Record<string, unknown>
          )
        } catch (error) {
          LogService.error('Send SMS API call failed', error)
        }
      },
    }),

    // ✅ 註冊完成
    registerComplete: builder.mutation<IRegisterCompleteResponse, IRegisterCompleteParams>({
      query: (params) => ({
        url: ConstantUtil.API_USER_REGISTER,
        method: 'POST',
        body: params,
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          LogService.logApiCall(
            ConstantUtil.API_USER_REGISTER,
            'POST',
            undefined,
            data as unknown as Record<string, unknown>
          )
        } catch (error) {
          LogService.error('Register complete API call failed', error)
        }
      },
    }),
    // 忘記密碼（發送重設連結）
    forgotPassword: builder.mutation<{ success: boolean; message: string }, { emailOrPhone: string; method: 'email' | 'sms' }>({
      query: (params) => ({
        url: ConstantUtil.API_USER_FORGOT_PASSWORD,
        method: 'POST',
        body: params,
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          LogService.logApiCall(
            ConstantUtil.API_USER_FORGOT_PASSWORD,
            'POST',
            undefined,
            data as unknown as Record<string, unknown>
          )
        } catch (error) {
          LogService.error('Forgot password API call failed', error)
        }
      },
    }),
    // 重設密碼
    resetPassword: builder.mutation<{ success: boolean; message: string }, { token: string; newPassword: string }>({
      query: (params) => ({
        url: ConstantUtil.API_USER_RESET_PASSWORD,
        method: 'POST',
        body: params,
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          LogService.logApiCall(
            ConstantUtil.API_USER_RESET_PASSWORD,
            'POST',
            undefined,
            data as unknown as Record<string, unknown>
          )
        } catch (error) {
          LogService.error('Reset password API call failed', error)
        }
      },
    }),
    // 修改密碼
    changePassword: builder.mutation<
      { success: boolean; message: string },
      { currentPassword: string; newPassword: string; confirmPassword: string }
    >({
      query: (params) => ({
        url: ConstantUtil.API_USER_CHANGE_PASSWORD,
        method: 'POST',
        body: params,
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          LogService.logApiCall(
            ConstantUtil.API_USER_CHANGE_PASSWORD,
            'POST',
            undefined,
            data as unknown as Record<string, unknown>
          );
        } catch (error) {
          LogService.error('Change password API call failed', error);
        }
      },
    }),

    // 取得用戶資料
    getUserProfile: builder.query<IUserProfile, void>({
      query: () => ConstantUtil.API_USER_PROFILE,
      providesTags: ['User'],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          LogService.logApiCall(
            ConstantUtil.API_USER_PROFILE,
            'GET',
            undefined,
            data as unknown as Record<string, unknown>
          )
        } catch (error) {
          LogService.error('Get user profile API call failed', error)
        }
      },
    }),
    // 更新用戶資料
    updateUserProfile: builder.mutation<IUpdateUserProfileResponse, IUpdateUserProfileParams>({
      query: (params) => ({
        url: ConstantUtil.API_USER_PROFILE, // 例：/api/v1/members/profile
        method: 'PATCH',
        body: params,
      }),
      invalidatesTags: ['User'],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          LogService.logApiCall(
            ConstantUtil.API_USER_PROFILE,
            'PATCH',
            undefined,
            data as unknown as Record<string, unknown>
          )
        } catch (error) {
          LogService.error('Update user profile API call failed', error)
        }
      },
    }),

    // 停用帳號
    deactivateAccount: builder.mutation<{ success: boolean; message: string }, void>({
      query: () => ({
        url: ConstantUtil.API_USER_DEACTIVATE,
        method: 'POST',
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          LogService.logApiCall(
            ConstantUtil.API_USER_DEACTIVATE,
            'POST',
            undefined,
            data as unknown as Record<string, unknown>
          )
        } catch (error) {
          LogService.error('Deactivate account API call failed', error)
        }
      },
    }),

    // 登出
    logout: builder.mutation<{ success: boolean; message: string }, void>({
      query: () => ({
        url: ConstantUtil.API_USER_LOGOUT,
        method: 'POST',
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const token = localStorage.getItem(ConstantUtil.STORAGE_USER_TOKEN)
          if (token) {
            try {
              const tokenPayload = JSON.parse(atob(token.split('.')[1]))
              LogService.info('登出 Token 追蹤', {
                userId: tokenPayload.sub || tokenPayload.id,
                userType: tokenPayload.type || 'unknown',
                logoutTime: new Date().toISOString(),
              })
            } catch (error) {
              LogService.warn('無法解析登出 token', { error: (error as Error).message })
            }
          }
          await queryFulfilled
          localStorage.removeItem(ConstantUtil.STORAGE_USER_TOKEN)
          localStorage.removeItem(ConstantUtil.STORAGE_USER_REFRESH_TOKEN)
          localStorage.removeItem(ConstantUtil.STORAGE_USER_INFO)
          LogService.info('User logged out successfully')
        } catch (error) {
          LogService.error('Logout failed', error)
        }
      },
      invalidatesTags: ['User'],
    }),
  }),
})

// ===============================
// 🪄 Hooks
// ===============================
export const {
  useLoginMutation,
  useGetUserProfileQuery,
  useUpdateUserProfileMutation,
  useChangePasswordMutation,
  useLogoutMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useRegisterStartMutation,
  useSendSmsVerificationMutation,
  useRegisterCompleteMutation, 
  useDeactivateAccountMutation
} = authApi
